import * as React from 'react';
import { Button, Card, Checkbox, DatePicker, Form, Input, Select, Spin, Typography } from 'antd';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import dayjs, { type Dayjs } from 'dayjs';
import { createReport } from './api';
import HistoryTable, { HISTORY_QUERY_KEY } from './HistoryTable';

const { RangePicker } = DatePicker;

type FormValues = {
    platform: string;
    account: string;
    all_time?: boolean;
    range?: [Dayjs, Dayjs];
};

const SocialMetricsPage: React.FC = () => {
    const [form] = Form.useForm<FormValues>();
    const allTime = Form.useWatch('all_time', form);
    const navigate = useNavigate();
    const qc = useQueryClient();
    const create = useMutation({
        mutationFn: createReport,
        onSuccess: (report) => {
            qc.invalidateQueries({ queryKey: HISTORY_QUERY_KEY });
            navigate(`/reporting/social-metrics/${report.id}`);
        },
    });

    const onFinish = (values: FormValues) => {
        const [start, end] = values.range ?? [];
        create.mutate({
            platform: values.platform,
            account: values.account.trim(),
            start_date: values.all_time ? undefined : start?.format('YYYY-MM-DD'),
            end_date: values.all_time ? undefined : end?.format('YYYY-MM-DD'),
        });
    };

    return (
        <div>
            <Typography.Title level={3} style={{ marginTop: 0 }}>Social metrics</Typography.Title>
            <Typography.Paragraph type="secondary">
                One platform, one account, and a date range or all time. The report is saved here. Excel is only generated when you download it.
            </Typography.Paragraph>
            <Spin spinning={create.isLoading} tip="This will take several minutes. Please wait.">
                <Card>
                    <Form
                        form={form}
                        layout="vertical"
                        onFinish={onFinish}
                        initialValues={{ platform: 'youtube' }}
                        disabled={create.isLoading}
                    >
                        <Form.Item name="platform" label="Platform" rules={[{ required: true, message: 'Choose a platform' }]}>
                            <Select
                                options={[
                                    { value: 'youtube', label: 'YouTube' },
                                    { value: 'tiktok', label: 'TikTok' },
                                    { value: 'instagram', label: 'Instagram' },
                                ]}
                            />
                        </Form.Item>
                        <Form.Item
                            name="account"
                            label="Account"
                            rules={[{ required: true, message: 'Enter a handle, channel ID, or profile URL' }]}
                        >
                            <Input placeholder="@handle, channel ID, or profile URL" />
                        </Form.Item>
                        <Form.Item name="all_time" valuePropName="checked">
                            <Checkbox>All time</Checkbox>
                        </Form.Item>
                        <Form.Item
                            name="range"
                            label="Date range"
                            rules={allTime ? [] : [{ required: true, message: 'Choose a date range' }]}
                        >
                            <RangePicker
                                disabled={!!allTime}
                                style={{ width: '100%', maxWidth: 360 }}
                                disabledDate={(d) => d.isAfter(dayjs(), 'day')}
                            />
                        </Form.Item>
                        <Button type="primary" htmlType="submit" loading={create.isLoading}>
                            Create report
                        </Button>
                    </Form>
                </Card>
            </Spin>
            <HistoryTable />
        </div>
    );
};

export default SocialMetricsPage;
